import { Link, useLocation } from 'react-router-dom'
import { useDevModeStore } from '../../stores/devModeStore'
import type { GeneratorType } from '@creanizer/types'

interface GeneratorInfo {
  type: GeneratorType | string  // 開発中のものはstring
  title: string
  description: string
  icon: string
  devOnly?: boolean  // 開発者モード専用フラグ
}

const generators: GeneratorInfo[] = [
  {
    type: 'name',
    title: 'ネームジェネレータ',
    description: '人物や地名の名前を自動生成',
    icon: '👤',
  },
  {
    type: 'plot',
    title: 'プロットジェネレータ',
    description: '物語の構成を自動生成',
    icon: '📖',
  },
  {
    type: 'title',
    title: 'タイトルジェネレータ',
    description: '作品タイトルを自動生成',
    icon: '✨',
  },
  {
    type: 'character',
    title: 'キャラクター設定',
    description: 'キャラクターの詳細設定を生成',
    icon: '🎭',
  },
  {
    type: 'relationship',
    title: '人間関係ジェネレータ',
    description: 'キャラクター間の関係性を生成',
    icon: '🤝',
  },
  {
    type: 'wikipedia',
    title: '架空事典ジェネレータ',
    description: 'Wikipedia風の架空記事を生成',
    icon: '📚',
  },
  {
    type: 'imagePrompt',
    title: '画像プロンプトジェネレータ',
    description: 'AI画像生成用のプロンプトを作成',
    icon: '🎨',
  },
  {
    type: 'enemy',
    title: 'エネミー・怪異ジェネレータ',
    description: 'RPGの敵や都市伝説の怪異を生成',
    icon: '👹',
  },
  {
    type: 'item',
    title: 'アイテムジェネレータ',
    description: '日用品から武器防具まで様々なアイテムを生成',
    icon: '📦',
  },
  {
    type: 'worldbuilder',
    title: '世界観ビルダー',
    description: '物語の世界観を総合的に構築',
    icon: '🌍',
  },
  {
    type: 'skill',
    title: 'スキルジェネレータ',
    description: 'キャラクターの技能や特技を体系的に生成',
    icon: '⚡',
  },
  {
    type: 'community',
    title: 'コミュニティジェネレータ',
    description: '国・町・村などの共同体を詳細に構築',
    icon: '🏘️',
  },
  {
    type: 'society',
    title: 'ソサエティジェネレータ',
    description: '政府機関から宗教団体、少人数グループまで組織を生成',
    icon: '🏛️',
  },
  {
    type: 'scene',
    title: 'シーン描写ジェネレータ',
    description: '物語の場面を詳細に描写',
    icon: '🎬',
  },
  {
    type: 'dungeon',
    title: 'ダンジョンジェネレータ',
    description: 'RPG用のダンジョン構造を自動生成',
    icon: '🗝️',
  },
  {
    type: 'location',
    title: 'ロケーションジェネレータ',
    description: '冒険に適した屋外ロケーション/自然環境を詳細に生成',
    icon: '📍',
  },
  {
    type: 'building',
    title: '建造物ジェネレータ',
    description: '城からコンビニまで様々な建築物を詳細に生成',
    icon: '🏰',
  },
  
  {
    type: 'dialogue',
    title: '対話シミュレータ',
    description: 'キャラクター間の自然な会話を生成',
    icon: '💬',
  },

  // ========== 開発中のジェネレータ（開発者モードでのみ表示） ==========
  {
    type: 'whatif',
    title: 'What-ifシナリオジェネレータ',
    description: '「もしも」の設定から展開される物語を生成',
    icon: '🔄',
    devOnly: true,
  },
  {
    type: 'lyric',
    title: 'リリックジェネレータ',
    description: '様々な要素から歌詞を作成',
    icon: '🎵',
    devOnly: true,
  },
  {
    type: 'suno',
    title: 'Sunoプロンプトジェネレータ',
    description: '音楽生成AI Suno用のプロンプトと歌詞を生成',
    icon: '🎼',
  },
  {
    type: 'document',
    title: '文献ジェネレータ',
    description: '新聞記事、雑誌記事、手記、日記、報告書、ブログ、SNSなど作中の様々な文献を作成',
    icon: '📰',
  },
  {
    type: 'scenario',
    title: 'シナリオジェネレータ',
    description: 'TRPGやゲームのシナリオを作成。フォーマットの解析・保存機能付き',
    icon: '🎲',
    devOnly: true,
  },
  {
    type: 'summary',
    title: 'テキスト要約ツール',
    description: '小説やログなどのテキストファイルを要点を抑えコンパクトに纏めます',
    icon: '📝',
  },
  {
    type: 'event',
    title: 'イベントジェネレータ',
    description: '保存されたデータと連携して物語の中で発生する突発的な、あるいは計画的に発生するイベント生成',
    icon: '⚡',
    devOnly: true,
  },
  {
    type: 'textgame',
    title: 'テキストゲームツール',
    description: 'フリーモードと構造化モードで遊べる本格的なテキストアドベンチャーゲーム',
    icon: '🎮',
    devOnly: true,
  },
  {
    type: 'consultation',
    title: '相談ツール',
    description: '創作活動の相談を、AIまたはキャラクターと行うチャット形式のツール',
    icon: '💭',
  },
]

export function HomePage() {
  const { isDevMode } = useDevModeStore()
  const location = useLocation()
  
  // 開発者モードでない場合は開発中のジェネレータを除外
  const visibleGenerators = generators.filter(g => !g.devOnly || isDevMode)
  
  // 現在のURLパラメータを保持（開発者モードを維持）
  const currentParams = location.search
  
  return (
    <div>
      <section className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          CreaNizer - 創作活動支援ツール
        </h1>
        <p className="text-lg text-gray-600">
          AIを活用して、あなたの創作活動をサポートします
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          ジェネレータ一覧
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:grid-cols-3">
          {visibleGenerators.map((generator) => (
            <Link
              key={generator.type}
              to={`/generator/${generator.type}${currentParams}`}
              className="card hover:shadow-md transition-shadow relative p-3 sm:p-4 md:p-6"
            >
              {generator.devOnly && (
                <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-orange-500 text-white text-xs px-1 py-0.5 sm:px-2 sm:py-1 rounded">
                  開発中
                </div>
              )}
              <div className="text-2xl sm:text-3xl md:text-4xl mb-2 sm:mb-3 md:mb-4">{generator.icon}</div>
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                {generator.title}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{generator.description}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}